import React from 'react';
import { useTranslation } from 'react-i18next';
import { DataGrid, GridColDef, GridColumnHeaderParams, GridPaginationModel, GridValidRowModel } from '@mui/x-data-grid';
import { Tooltip } from '@mui/material';
import TooltipIcon from '@/components/svg/Tooltip';
import styles from './Table.module.scss';

// READ ME:
// Header tooltip: Use columns.description = 'This is tooltip'
// Row color: use rows.__className__ = styles.redRow
// pageSize must be 1 of pageSizeOptions if you want to use pageSizeOptions

// DataGrid (MIT) forces `pagination: true` and caps pageSize at 100, so a footerless
// `kind: 'Table'` silently truncates to the first 100 rows. Above that we keep the
// footer visible so the remaining rows stay reachable.
const MAX_PAGE_SIZE = 100;

// DataGrid merges our props over its defaults with Object.assign, which copies keys
// holding `undefined`. Passing `pageSizeOptions={undefined}` therefore wipes out the
// default and makes GridPagination throw on `pageSizeOptions.length`, so always pass
// a real array. Mirrors DATA_GRID_PROPS_DEFAULT_VALUES.pageSizeOptions.
const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, MAX_PAGE_SIZE];

export type TableKind =
  | {
      kind: 'Table';
    }
  | {
      kind: 'PaginationTable';
      pageSize: number;
      pageSizeOptions?: number[];
    }
  | {
      kind: 'DynamicTable';
      totalItem: number;
      pageSize: number;
      pageSizeOptions?: number[];
      onPageChange: (model: GridPaginationModel) => void;
    };

const View: React.FC<{
  rows: GridValidRowModel[];
  columns: GridColDef[];
  loading?: boolean;
  sx?: Record<string, React.CSSProperties>;
  table: TableKind;
  autoHeight?: boolean;
  shouldNotPaddingColumnHeader?: boolean;
  shouldUseSmallHeaderText?: boolean;
  scrollHorizontal?: boolean;
}> = ({
  rows,
  columns,
  loading,
  sx,
  table,
  autoHeight,
  shouldNotPaddingColumnHeader,
  shouldUseSmallHeaderText,
  scrollHorizontal,
}) => {
  const { t } = useTranslation();

  const updatedSx = {
    '& .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-columnHeaders': {
      minHeight: '0 !important',
      maxHeight: '1000px !important',
      height: 'auto !important',
      padding: shouldNotPaddingColumnHeader ? '' : '0.6rem 0 !important',
    },
    '& .MuiDataGrid-columnHeader': {
      height: 'auto !important',
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-virtualScroller': {
      overflowY: 'auto !important',
      overflowX: scrollHorizontal ? 'scroll' : 'hidden !important',
    },
    // '& .MuiDataGrid-virtualScrollerContent': {
    //   overflow: 'overlay',
    // },
    '& .MuiDataGrid-cell': {
      display: 'flex !important',
      alignItems: 'flex-start !important',
      padding: '0.6rem 1rem !important',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-row:hover': {
      background: 'transparent',
    },
    '& .MuiDataGrid-cellContent': {
      wordWrap: 'break-word',
      whiteSpace: 'break-spaces',
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .MuiDataGrid-row:last-child .MuiDataGrid-cell': {
      border: 'none',
    },
    '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:first-of-type': {
      paddingLeft: '1.1rem !important',
    },
    '& .MuiDataGrid-row .MuiDataGrid-cell:first-of-type': {
      paddingLeft: '1.5rem !important',
    },
    '& .MuiDataGrid-row .MuiDataGrid-cell:nth-last-of-type(1)': {
      paddingRight: '1.5rem !important',
    },
    ...sx,
  };

  const updatedColumns: GridColDef[] = columns.map((c) => ({
    cellClassName: c.cellClassName,
    sortable: false,
    disableColumnMenu: true,
    flex: scrollHorizontal ? undefined : 1,
    ...c,
    renderHeader: (params: GridColumnHeaderParams) => {
      const header = c.renderHeader ? c.renderHeader(params) : undefined;
      return (
        <div
          className={styles.headerCellWrapper}
          data-cy={'cell-header'}
        >
          {header || renderHeader(params, shouldUseSmallHeaderText)}
        </div>
      );
    },
    renderCell: (params) => {
      const cell = c.renderCell ? c.renderCell(params) : undefined;
      return (
        <div
          className={styles.cellWrapper}
          data-cy={'cell-value'}
        >
          {cell || params.value}
        </div>
      );
    },
  }));

  const pageSizeOptions =
    (table.kind === 'PaginationTable' || table.kind === 'DynamicTable') && table.pageSizeOptions
      ? table.pageSizeOptions
      : DEFAULT_PAGE_SIZE_OPTIONS;

  const isTruncatedPlainTable = table.kind === 'Table' && rows.length > MAX_PAGE_SIZE;

  const pageSize = getPageSize(table, isTruncatedPlainTable);

  const initialState = pageSize === undefined ? undefined : { pagination: { paginationModel: { pageSize } } };

  const onPaginationModelChange = (model: GridPaginationModel) => {
    if (table.kind === 'DynamicTable') {
      table.onPageChange(model);
    }
  };

  return (
    <div
      data-cy={'table-container'}
      className={styles.container}
    >
      <DataGrid
        sx={updatedSx}
        rows={rows}
        columns={updatedColumns}
        onPaginationModelChange={onPaginationModelChange}
        initialState={initialState}
        pageSizeOptions={pageSizeOptions}
        hideFooter={rows.length === 0 ? true : table.kind === 'Table' && !isTruncatedPlainTable}
        loading={loading ?? false}
        rowCount={table.kind === 'DynamicTable' ? table.totalItem : undefined}
        getRowClassName={(params) => params.row.__className__}
        getRowHeight={() => 'auto'}
        rowSelection={false}
        slots={{
          noRowsOverlay: () => <div className={styles.noItem}>{t('common.no_item')}</div>,
        }}
        paginationMode={table.kind === 'DynamicTable' ? 'server' : 'client'}
        autoHeight={autoHeight ?? true}
      />
    </div>
  );
};

function getPageSize(table: TableKind, isTruncatedPlainTable: boolean): number | undefined {
  if (table.kind === 'PaginationTable' || table.kind === 'DynamicTable') {
    return table.pageSize;
  }
  return isTruncatedPlainTable ? MAX_PAGE_SIZE : undefined;
}

function renderHeader(params: GridColumnHeaderParams, shouldUseSmallHeaderText?: boolean): JSX.Element {
  if (params.colDef.description) {
    return (
      <>
        {shouldUseSmallHeaderText ? (
          <div className={styles.headerCellSmallText}>{params.colDef.headerName}</div>
        ) : (
          <div className={styles.headerCell}>{params.colDef.headerName}</div>
        )}
        <Tooltip title={params.colDef.description}>
          <div>
            <TooltipIcon className={styles.tooltipIcon} />
          </div>
        </Tooltip>
      </>
    );
  } else {
    if (shouldUseSmallHeaderText) {
      return <div className={styles.headerCellSmallText}>{params.colDef.headerName}</div>;
    }
    return <div className={styles.headerCell}>{params.colDef.headerName}</div>;
  }
}

export default View;
